import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Alert, Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../redux-store";
import { sessionAnnounced } from "../ducks/auth";
import { selectSessionChangedElsewhere } from "../ducks/auth/selectors";
import {
  isSessionAnnouncement,
  sessionChannel,
} from "../helpers/broadcast-channel";

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// Where to reload to under the new session. The hash is always dropped, since
// it reopens dialogs for the previous account's records (a #cargo… hash is what
// 403'd in DISPATCHER-CX). A path or query naming a record by id points at the
// previous account's data too, so that goes back to the start page instead;
// pages without ids (dispatching, upcoming, live view…) are kept.
export const reloadTarget = (location: { pathname: string; search: string }) =>
  UUID_RE.test(location.pathname + location.search)
    ? "/"
    : location.pathname + location.search;

// Shown when another tab logged into a different account: the shared session
// cookie now belongs to that account, so this tab's data is stale and its
// requests are refused. Not dismissable, since the tab can't work again
// without a reload, but the page stays usable so unsaved input can be copied.
export default function SessionChangedBanner() {
  const { t } = useTranslation(["translation", "components"]);
  const dispatch = useAppDispatch();
  const sessionChanged = useSelector(selectSessionChangedElsewhere);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (isSessionAnnouncement(event.data)) {
        dispatch(sessionAnnounced(event.data.userId));
      }
    };
    sessionChannel.addEventListener("message", onMessage);
    return () => sessionChannel.removeEventListener("message", onMessage);
  }, [dispatch]);

  if (!sessionChanged) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
      }}
    >
      <Alert
        severity="warning"
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={() => window.location.assign(reloadTarget(window.location))}
          >
            {t("components:buttonLabels.refresh")}
          </Button>
        }
      >
        {t("translation:sessionChangedInOtherTab")}
      </Alert>
    </Box>
  );
}
